import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import DashLayout from "@/components/DashLayout";
import api from "@/lib/api";
import { toast } from "sonner";
import { NotebookPen, Plus, Calendar, Sparkles, Trash2, Edit3, Award, X, BookOpen, FileText, ClipboardCheck } from "lucide-react";

const KIND_META = {
  daily:    { label: "Daily",    icon: Calendar,       tone: "#f47a20", desc: "Short end-of-day reflection." },
  weekly:   { label: "Weekly",   icon: Sparkles,       tone: "#3B82F6", desc: "What did the week teach you?" },
  cpd:      { label: "CPD",      icon: BookOpen,       tone: "#22C55E", desc: "After a course, workshop or webinar." },
  case:     { label: "Case",     icon: ClipboardCheck, tone: "#EAB308", desc: "Patient case learning." },
  incident: { label: "Incident", icon: FileText,       tone: "#EF4444", desc: "Adverse event or near-miss." },
};

const EMPTY = {
  title: "",
  kind: "daily",
  activity_date: new Date().toISOString().slice(0, 10),
  description: "",
  what_went_well: "",
  what_could_improve: "",
  learning: "",
  application: "",
  impact: "",
  standards: [],
  cpd_hours: 0,
  linked_cpd_log_id: null,
  tags: [],
};

export default function ClinicianReflections() {
  const { t } = useTranslation();
  // Localised kind meta — pulls translated labels for filter chips, badges and form description helper text.
  const kindMeta = {
    daily:    { ...KIND_META.daily,    label: t("reflections.filterDaily"),    desc: t("reflections.dailyDesc") },
    weekly:   { ...KIND_META.weekly,   label: t("reflections.filterWeekly"),   desc: t("reflections.weeklyDesc") },
    cpd:      { ...KIND_META.cpd,      label: t("reflections.filterCpd"),      desc: t("reflections.cpdDesc") },
    case:     { ...KIND_META.case,     label: t("reflections.filterCase"),     desc: t("reflections.caseDesc") },
    incident: { ...KIND_META.incident, label: t("reflections.filterIncident"), desc: t("reflections.incidentDesc") },
  };
  const [items, setItems] = useState([]);
  const [totals, setTotals] = useState({ total_hours: 0, ytd_hours: 0, count: 0 });
  const [filter, setFilter] = useState("all");
  const [standards, setStandards] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [expanded, setExpanded] = useState(null);

  const load = async () => {
    try {
      const { data } = await api.get("/reflections/mine");
      setItems(data.items || []);
      setTotals({ total_hours: data.total_hours || 0, ytd_hours: data.ytd_hours || 0, count: data.count || 0 });
    } catch { toast.error("Could not load reflections"); }
  };
  useEffect(() => {
    load();
    api.get("/reflections/standards").then((r) => setStandards(r.data)).catch(() => {});
  }, []);

  const openNew = () => { setForm(EMPTY); setEditingId(null); setShowForm(true); };
  const openEdit = (r) => {
    setForm({
      title: r.title || "", kind: r.kind || "daily", activity_date: r.activity_date || new Date().toISOString().slice(0, 10),
      description: r.description || "", what_went_well: r.what_went_well || "", what_could_improve: r.what_could_improve || "",
      learning: r.learning || "", application: r.application || "", impact: r.impact || "",
      standards: r.standards || [], cpd_hours: r.cpd_hours || 0, linked_cpd_log_id: r.linked_cpd_log_id || null, tags: r.tags || [],
    });
    setEditingId(r.id); setShowForm(true);
  };

  const submit = async () => {
    if (!form.title.trim()) return toast.error("Title is required");
    if (!form.description.trim()) return toast.error("Describe what happened");
    if (!form.learning.trim()) return toast.error("What did you learn?");
    if (!form.application.trim()) return toast.error("How will you apply this?");
    setSaving(true);
    try {
      if (editingId) {
        await api.patch(`/reflections/${editingId}`, form);
        toast.success("Reflection updated");
      } else {
        await api.post("/reflections", form);
        toast.success("Reflection saved");
      }
      setShowForm(false); setEditingId(null); setForm(EMPTY);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not save reflection");
    }
    setSaving(false);
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this reflection? This cannot be undone.")) return;
    try {
      await api.delete(`/reflections/${id}`);
      toast.success("Deleted");
      load();
    } catch { toast.error("Could not delete"); }
  };

  const filtered = useMemo(() => filter === "all" ? items : items.filter((i) => i.kind === filter), [items, filter]);
  const standardByCode = useMemo(() => Object.fromEntries(standards.map((s) => [s.code, s])), [standards]);

  return (
    <DashLayout>
      <div className="mb-8 flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="fl-label text-[#f47a20]">{t("reflections.pageEyebrow")}</div>
          <h1 className="fl-heading text-4xl md:text-5xl mt-2">{t("reflections.pageH1")}</h1>
          <p className="mt-2 text-sm max-w-2xl" style={{ color: "var(--fl-muted)" }}>
            {t("reflections.pageSubtitle")}
          </p>
        </div>
        <button className="fl-btn" onClick={openNew} data-testid="new-reflection-btn"><Plus size={16} /> {t("reflections.newBtn")}</button>
      </div>

      {/* Stats strip */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-8">
        <div className="fl-card p-4" data-testid="ref-stat-total">
          <NotebookPen className="text-[#f47a20]" size={16} />
          <div className="fl-label mt-2">{t("reflections.statTotal")}</div>
          <div className="fl-heading text-3xl mt-1">{totals.count}</div>
        </div>
        <div className="fl-card p-4" data-testid="ref-stat-ytd">
          <Award className="text-[#f47a20]" size={16} />
          <div className="fl-label mt-2">{t("reflections.statYtd")}</div>
          <div className="fl-heading text-3xl mt-1">{totals.ytd_hours}<span className="text-sm ml-1" style={{ color: "var(--fl-muted-2)" }}>h</span></div>
        </div>
        <div className="fl-card p-4" data-testid="ref-stat-all-hours">
          <BookOpen className="text-[#f47a20]" size={16} />
          <div className="fl-label mt-2">{t("reflections.statAllHours")}</div>
          <div className="fl-heading text-3xl mt-1">{totals.total_hours}<span className="text-sm ml-1" style={{ color: "var(--fl-muted-2)" }}>h</span></div>
        </div>
        <div className="fl-card p-4" data-testid="ref-stat-last">
          <Calendar className="text-[#f47a20]" size={16} />
          <div className="fl-label mt-2">{t("reflections.statLast")}</div>
          <div className="fl-heading text-lg mt-1">
            {items[0]?.activity_date ? new Date(items[0].activity_date).toLocaleDateString("en-GB", { day: "numeric", month: "short" }) : "—"}
          </div>
        </div>
      </div>

      {/* Filter chips */}
      <div className="flex flex-wrap gap-2 mb-6">
        <button onClick={() => setFilter("all")} className={`px-3 py-1.5 rounded text-xs uppercase tracking-wider border ${filter === "all" ? "border-[#f47a20] text-[#f47a20]" : "border-white/10"}`} style={filter === "all" ? {} : { color: "var(--fl-muted)" }} data-testid="ref-filter-all">{t("reflections.filterAll")}</button>
        {Object.entries(kindMeta).map(([k, meta]) => {
          const Icon = meta.icon;
          return (
            <button key={k} onClick={() => setFilter(k)}
              className={`px-3 py-1.5 rounded text-xs uppercase tracking-wider border flex items-center gap-1.5 ${filter === k ? "border-[#f47a20] text-[#f47a20]" : "border-white/10"}`}
              style={filter === k ? {} : { color: "var(--fl-muted)" }}
              data-testid={`ref-filter-${k}`}>
              <Icon size={12} /> {meta.label}
            </button>
          );
        })}
      </div>

      {/* Reflection list */}
      {filtered.length === 0 ? (
        <div className="fl-card p-10 text-center" data-testid="ref-empty">
          <NotebookPen size={28} className="text-[#f47a20] mx-auto" />
          <div className="fl-heading text-xl mt-4">{t("reflections.emptyTitle")}</div>
          <div className="text-sm mt-1" style={{ color: "var(--fl-muted)" }}>{t("reflections.emptyBody")}</div>
          <button className="fl-btn mt-5" onClick={openNew} data-testid="ref-empty-cta"><Plus size={14} /> {t("reflections.emptyCta")}</button>
        </div>
      ) : (
        <div className="space-y-3">
          {filtered.map((r) => {
            const meta = kindMeta[r.kind] || kindMeta.daily;
            const Icon = meta.icon;
            const isOpen = expanded === r.id;
            return (
              <div key={r.id} className="fl-card p-5" data-testid={`ref-item-${r.id}`}>
                <div className="flex items-start justify-between gap-3 flex-wrap">
                  <div className="flex items-start gap-3 min-w-0 flex-1">
                    <div className="w-10 h-10 rounded-md flex items-center justify-center shrink-0" style={{ background: `${meta.tone}15`, color: meta.tone }}>
                      <Icon size={18} />
                    </div>
                    <div className="min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="text-[10px] uppercase tracking-wider font-bold" style={{ color: meta.tone }}>{meta.label}</span>
                        <span className="text-xs" style={{ color: "var(--fl-muted-2)" }}>{new Date(r.activity_date).toLocaleDateString("en-GB", { weekday: "short", day: "numeric", month: "short", year: "numeric" })}</span>
                        {r.cpd_hours > 0 && <span className="text-xs text-[#f47a20]">{r.cpd_hours}h CPD</span>}
                      </div>
                      <div className="fl-heading text-lg mt-1">{r.title}</div>
                      <div className="text-sm mt-1 line-clamp-2" style={{ color: "var(--fl-muted)" }}>{r.description}</div>
                    </div>
                  </div>
                  <div className="flex items-center gap-1">
                    <button className="fl-btn-ghost text-xs" onClick={() => setExpanded(isOpen ? null : r.id)} data-testid={`ref-toggle-${r.id}`}>
                      {isOpen ? t("reflections.hide") : t("reflections.view")}
                    </button>
                    <button className="fl-btn-ghost text-xs" onClick={() => openEdit(r)} title={t("common.edit")} data-testid={`ref-edit-${r.id}`}><Edit3 size={12} /></button>
                    <button className="fl-btn-ghost text-xs" onClick={() => remove(r.id)} title={t("common.delete")} data-testid={`ref-delete-${r.id}`}><Trash2 size={12} /></button>
                  </div>
                </div>

                {isOpen && (
                  <div className="mt-4 pt-4 border-t space-y-3 text-sm" style={{ borderColor: "var(--fl-border)" }}>
                    {r.what_went_well && (<Field label={t("reflections.fWentWell")} value={r.what_went_well} />)}
                    {r.what_could_improve && (<Field label={t("reflections.fCouldImprove")} value={r.what_could_improve} />)}
                    <Field label={t("reflections.fLearning")} value={r.learning} />
                    <Field label={t("reflections.fApplication")} value={r.application} />
                    {r.impact && <Field label={t("reflections.fImpact")} value={r.impact} />}
                    {r.standards?.length > 0 && (
                      <div>
                        <div className="fl-label mb-1">{t("reflections.fStandards")}</div>
                        <div className="flex flex-wrap gap-1.5">
                          {r.standards.map((code) => (
                            <span key={code} className="text-[10px] px-2 py-1 rounded border" style={{ borderColor: "var(--fl-border)", color: "var(--fl-muted)" }} title={standardByCode[code]?.label}>
                              #{code} · {standardByCode[code]?.label?.split(":")[0]?.slice(0, 40) || "HCPC"}
                            </span>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {showForm && (
        <ReflectionForm
          form={form} setForm={setForm}
          standards={standards}
          saving={saving}
          editing={!!editingId}
          kindMeta={kindMeta}
          onCancel={() => { setShowForm(false); setEditingId(null); setForm(EMPTY); }}
          onSubmit={submit}
        />
      )}
    </DashLayout>
  );
}

function Field({ label, value }) {
  return (
    <div>
      <div className="fl-label mb-1">{label}</div>
      <div style={{ color: "var(--fl-text)" }} className="whitespace-pre-wrap">{value}</div>
    </div>
  );
}

function ReflectionForm({ form, setForm, standards, saving, editing, kindMeta, onCancel, onSubmit }) {
  const { t } = useTranslation();
  const toggleStandard = (code) => {
    setForm((f) => ({ ...f, standards: f.standards.includes(code) ? f.standards.filter((c) => c !== code) : [...f.standards, code] }));
  };
  return (
    <div className="fixed inset-0 z-50 bg-black/85 backdrop-blur-sm overflow-y-auto p-4 flex items-start md:items-center justify-center" data-testid="reflection-form">
      <div className="fl-card w-full max-w-3xl">
        <div className="flex items-center justify-between p-4 border-b" style={{ borderColor: "var(--fl-border)" }}>
          <div>
            <div className="fl-label text-[#f47a20]">{t("reflections.formEyebrow")}</div>
            <div className="fl-heading text-xl mt-1">{editing ? t("reflections.formEdit") : t("reflections.formNew")}</div>
          </div>
          <button onClick={onCancel} className="fl-btn-ghost text-xs" data-testid="ref-form-close"><X size={16} /></button>
        </div>

        <div className="p-6 space-y-4">
          <div className="grid md:grid-cols-3 gap-3">
            <div>
              <div className="fl-label mb-1.5">{t("reflections.formType")}</div>
              <select className="fl-input" value={form.kind} onChange={(e) => setForm({ ...form, kind: e.target.value })} data-testid="ref-form-kind">
                {Object.entries(kindMeta).map(([k, m]) => (
                  <option key={k} value={k}>{m.label}</option>
                ))}
              </select>
              <div className="text-[11px] mt-1" style={{ color: "var(--fl-muted-2)" }}>{kindMeta[form.kind]?.desc}</div>
            </div>
            <div>
              <div className="fl-label mb-1.5">{t("reflections.formDate")}</div>
              <input type="date" className="fl-input" value={form.activity_date} onChange={(e) => setForm({ ...form, activity_date: e.target.value })} data-testid="ref-form-date" />
            </div>
            <div>
              <div className="fl-label mb-1.5">{t("reflections.formHours")}</div>
              <input type="number" min="0" step="0.5" className="fl-input" value={form.cpd_hours} onChange={(e) => setForm({ ...form, cpd_hours: parseFloat(e.target.value || 0) })} data-testid="ref-form-hours" />
            </div>
          </div>

          <div>
            <div className="fl-label mb-1.5">{t("reflections.formTitle")}</div>
            <input className="fl-input" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })}
              placeholder={t("reflections.formTitlePh")}
              data-testid="ref-form-title" />
          </div>

          <div>
            <div className="fl-label mb-1.5">{t("reflections.formDesc")}</div>
            <textarea className="fl-input" rows="3" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })}
              placeholder={t("reflections.formDescPh")}
              data-testid="ref-form-description" />
          </div>

          <div className="grid md:grid-cols-2 gap-3">
            <div>
              <div className="fl-label mb-1.5">{t("reflections.fWentWell")}</div>
              <textarea className="fl-input" rows="3" value={form.what_went_well} onChange={(e) => setForm({ ...form, what_went_well: e.target.value })}
                placeholder={t("reflections.formWellPh")}
                data-testid="ref-form-well" />
            </div>
            <div>
              <div className="fl-label mb-1.5">{t("reflections.fCouldImprove")}</div>
              <textarea className="fl-input" rows="3" value={form.what_could_improve} onChange={(e) => setForm({ ...form, what_could_improve: e.target.value })}
                placeholder={t("reflections.formImprovePh")}
                data-testid="ref-form-improve" />
            </div>
          </div>

          <div>
            <div className="fl-label mb-1.5">{t("reflections.formLearningLbl")}</div>
            <textarea className="fl-input" rows="3" value={form.learning} onChange={(e) => setForm({ ...form, learning: e.target.value })}
              placeholder={t("reflections.formLearningPh")}
              data-testid="ref-form-learning" />
          </div>

          <div>
            <div className="fl-label mb-1.5">{t("reflections.formApplicationLbl")}</div>
            <textarea className="fl-input" rows="3" value={form.application} onChange={(e) => setForm({ ...form, application: e.target.value })}
              placeholder={t("reflections.formApplicationPh")}
              data-testid="ref-form-application" />
          </div>

          <div>
            <div className="fl-label mb-1.5">{t("reflections.formImpactLbl")}</div>
            <textarea className="fl-input" rows="2" value={form.impact} onChange={(e) => setForm({ ...form, impact: e.target.value })}
              placeholder={t("reflections.formImpactPh")}
              data-testid="ref-form-impact" />
          </div>

          <div>
            <div className="fl-label mb-2">{t("reflections.formStandardsLbl")}</div>
            <div className="grid md:grid-cols-2 gap-1.5 max-h-48 overflow-y-auto pr-2">
              {standards.map((s) => (
                <label key={s.code} className="flex items-start gap-2 text-xs cursor-pointer p-1.5 rounded hover:bg-white/5" data-testid={`ref-standard-${s.code}`}>
                  <input type="checkbox" checked={form.standards.includes(s.code)} onChange={() => toggleStandard(s.code)} className="mt-0.5 accent-[#f47a20]" />
                  <div><span className="text-[#f47a20] font-bold">#{s.code}</span> <span style={{ color: "var(--fl-muted)" }}>{s.label}</span></div>
                </label>
              ))}
            </div>
          </div>
        </div>

        <div className="flex justify-end gap-2 p-4 border-t" style={{ borderColor: "var(--fl-border)" }}>
          <button className="fl-btn-ghost" onClick={onCancel} data-testid="ref-form-cancel">{t("reflections.btnCancel")}</button>
          <button className="fl-btn" onClick={onSubmit} disabled={saving} data-testid="ref-form-save">
            {saving ? t("reflections.btnSaving") : editing ? t("reflections.btnUpdate") : t("reflections.btnSave")}
          </button>
        </div>
      </div>
    </div>
  );
}
